import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'components/UI/Modal/Modal'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { SalesStage } from 'models/sales_stages'
import {
  StageDragAndDropItem,
  StageDragOverlayItem,
  StageLockedRow,
} from './StageDragAndDropItem'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { PiArrowCounterClockwise, PiArrowsDownUp, PiPlus } from 'react-icons/pi'
import { Toggle } from 'components/FbUI/Toggle'
import dialogConfirm from 'components/dialogConfirm'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { useChangeMiscSettings } from 'services/preferences'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import {
  getStageColor,
  isTerminalStage,
} from 'components/SalesStage/stageColors'

interface ReorderStagesModalProps {
  stages: SalesStage[]
  onSave: (
    orderedStageIds: number[],
    /** Ids the user staged for deletion in the modal; deleted on Save. */
    deletedStageIds?: number[]
  ) => Promise<void>
  /** Opens the create-stage modal. Called after this modal closes. */
  onCreateStage?: () => void
  /** Opens the edit-stage modal for a stage. Called after this modal closes. */
  onEditStage?: (stageId: number) => void
}

/**
 * Imperative handle so a parent can reopen this modal after the create/edit
 * flow it handed off to completes. `open()` re-seeds from the latest stages;
 * pass `stagesOverride` to seed from a freshly-refetched list without waiting
 * for a prop re-render.
 */
export interface ReorderStagesModalHandle {
  open: (stagesOverride?: SalesStage[]) => void
}

/**
 * A stage can only be deleted once nothing references it. Mirrors the block in
 * SalesStageTable.handleRowDelete. Returns the human-readable reason it's
 * blocked, or null when the stage is safe to delete.
 */
function deleteBlockReason(stage: SalesStage): string | null {
  const parts: string[] = []
  if (stage.deals_count > 0) {
    parts.push(`${stage.deals_count} deal${stage.deals_count !== 1 ? 's' : ''}`)
  }
  if (stage.automations_count > 0) {
    parts.push(
      `${stage.automations_count} automation${stage.automations_count !== 1 ? 's' : ''}`
    )
  }
  if (parts.length === 0) return null
  return `Can't delete — still used by ${parts.join(' and ')}. Reassign them first.`
}

/** Quoted, comma-joined name list: `"A"` / `"A" and "B"` / `"A", "B", and "C"`. */
function formatStageNameList(names: string[]): string {
  const quoted = names.map((n) => `"${n}"`)
  if (quoted.length <= 1) return quoted[0] ?? ''
  if (quoted.length === 2) return `${quoted[0]} and ${quoted[1]}`
  return `${quoted.slice(0, -1).join(', ')}, and ${quoted[quoted.length - 1]}`
}

/** Shared treatment for the modal's footer text-link buttons (Create / Reset). */
const textLinkClass =
  'inline-flex items-center gap-1.5 text-sm font-semibold text-brand-bright transition-colors hover:text-brand-active disabled:cursor-not-allowed disabled:opacity-50'

export const ReorderStagesModal = forwardRef<
  ReorderStagesModalHandle,
  ReorderStagesModalProps
>(function ReorderStagesModal(
  { stages, onSave, onCreateStage, onEditStage },
  ref
) {
  const [open, setOpen] = useState(false)
  // Ordered ids of the non-terminal (draggable) stages — the source of truth
  // for pipeline sequence while the modal is open.
  const [orderedIds, setOrderedIds] = useState<number[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  // Stages removed in this session: pulled from the list immediately, deleted
  // on Save.
  const [deletedIds, setDeletedIds] = useState<number[]>([])
  const { isSettingEnabled } = usePreferences()
  const { mutate: changeMiscSettings } = useChangeMiscSettings()
  const queryClient = useQueryClient()

  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const stagesById = useMemo(
    () => new Map(stages.map((s) => [s.id, s])),
    [stages]
  )

  /** Non-terminal stage ids, in pipeline order, from a source list. */
  const regularIdsOf = (list: SalesStage[]): number[] =>
    list
      .filter((s) => !isTerminalStage(s))
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => s.id)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleOpen = (stagesOverride?: SalesStage[]) => {
    setOrderedIds(regularIdsOf(stagesOverride ?? stages))
    setDeletedIds([])
    setOpen(true)
  }

  useImperativeHandle(ref, () => ({
    open: (stagesOverride?: SalesStage[]) => handleOpen(stagesOverride),
  }))

  const handleClose = () => {
    setOpen(false)
    setOrderedIds([])
    setActiveId(null)
    setDeletedIds([])
  }

  /** Stage a deletion: drop the row now, commit it on Save. */
  const handleDeleteStage = (stageId: number) => {
    setOrderedIds((prev) => prev.filter((id) => id !== stageId))
    setDeletedIds((prev) =>
      prev.includes(stageId) ? prev : [...prev, stageId]
    )
  }

  /** Close this modal and hand off to the parent's create-stage modal. */
  const handleCreateStage = () => {
    handleClose()
    onCreateStage?.()
  }

  /** Close this modal and hand off to the parent's edit-stage modal; the parent
   *  reopens Organize on save (same round-trip as create). */
  const handleEditStage = (stageId: number) => {
    handleClose()
    onEditStage?.(stageId)
  }

  const handleToggleCustomOrder = () => {
    changeMiscSettings({
      key: 'sales_stage_custom_order',
      data: { value: customOrderEnabled ? 'false' : 'true' },
    })
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    setOrderedIds((prev) => {
      const from = prev.indexOf(active.id as number)
      const to = prev.indexOf(over.id as number)
      if (from < 0 || to < 0) return prev
      return arrayMove(prev, from, to)
    })
  }

  const terminalStages = stages.filter((s) => isTerminalStage(s))
  const terminalIds = terminalStages.map((s) => s.id)

  const handleSave = async () => {
    // Staged deletions are permanent once Save runs — confirm at the commit
    // point (one prompt, regardless of how many were staged).
    if (deletedIds.length > 0) {
      const count = deletedIds.length
      const noun = `stage${count !== 1 ? 's' : ''}`
      const names = deletedIds
        .map((id) => stagesById.get(id)?.stage)
        .filter((n): n is string => Boolean(n))
      const subject =
        names.length === count ? formatStageNameList(names) : `${count} ${noun}`
      const confirmed = await dialogConfirm(
        `This will permanently delete ${subject}. This can't be undone.`,
        `Delete ${count} ${noun}?`,
        '',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )
      if (!confirmed) return
    }
    setIsSaving(true)
    try {
      await onSave([...orderedIds, ...terminalIds], deletedIds)
      queryClient.invalidateQueries({ queryKey: ['preferences'] })
      handleClose()
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetToDefault = async () => {
    setIsSaving(true)
    try {
      // Default order: win_rate ASC (same as the backfill migration), then let
      // the app derive colors from position.
      const regulars = stages
        .filter((s) => !isTerminalStage(s))
        .sort((a, b) => (a.win_rate ?? 0) - (b.win_rate ?? 0))
      const orderedDefault = [...regulars.map((s) => s.id), ...terminalIds]
      await onSave(orderedDefault)
      changeMiscSettings({
        key: 'sales_stage_custom_order',
        data: { value: 'false' },
      })
      toast.info('Sales stages reset to default order')
      handleClose()
    } finally {
      setIsSaving(false)
    }
  }

  const activeStage = activeId != null ? stagesById.get(activeId) : undefined
  const rows = orderedIds
    .map((id) => stagesById.get(id))
    .filter((s): s is SalesStage => Boolean(s))

  return (
    <Ds2026ButtonProvider>
      <FbButton variant="secondary" onClick={() => handleOpen()}>
        <PiArrowsDownUp size={16} />
        Organize Stages
      </FbButton>
      <Modal
        open={open}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
        title="Organize Sales Stages"
        description="Drag to set the pipeline sequence. Each stage's badge color is set per stage (use Edit), or auto-assigned by position."
        onAccept={handleSave}
        acceptButtonText="Save"
        cancelButtonText="Cancel"
        loading={isSaving}
        size="lg"
      >
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-800">
              Apply custom order across the app
            </span>
            <span className="text-xs text-gray-500">
              When on, this sequence drives deal cards, the deals table, and the
              sales pipeline.
            </span>
          </div>
          <Toggle
            size="md"
            checked={customOrderEnabled}
            onChange={handleToggleCustomOrder}
            aria-label="Apply custom order across the app"
          />
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="mt-4 flex max-h-[440px] flex-col gap-2 overflow-y-auto pr-1">
            <SortableContext
              items={orderedIds}
              strategy={verticalListSortingStrategy}
            >
              {rows.map((stage) => {
                const blockedReason = deleteBlockReason(stage)
                return (
                  <StageDragAndDropItem
                    key={stage.id}
                    id={stage.id}
                    stage={stage}
                    dotClassName={
                      getStageColor(stage, stages, customOrderEnabled).dot
                    }
                    onEdit={
                      onEditStage ? () => handleEditStage(stage.id) : undefined
                    }
                    onDelete={() => handleDeleteStage(stage.id)}
                    canDelete={!blockedReason}
                    deleteDisabledReason={blockedReason ?? undefined}
                  />
                )
              })}
            </SortableContext>

            {/* Terminal stages — fixed outcomes, colored by name, not
                  reorderable. They sit at the bottom of the pipeline. */}
            {terminalStages.length > 0 && (
              <div className="mt-1">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Closed outcomes
                </p>
                <div className="flex flex-col gap-2">
                  {terminalStages.map((stage) => (
                    <StageLockedRow key={stage.id} stage={stage} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* The modal centers itself with a CSS transform, which would
                otherwise become the containing block for the overlay's
                position:fixed and make the dragged card fly off. Portaling to
                <body> keeps the overlay's coordinates in viewport space. */}
          {createPortal(
            <DragOverlay>
              {activeStage ? (
                <StageDragOverlayItem
                  stage={activeStage}
                  dotClassName={
                    getStageColor(activeStage, stages, customOrderEnabled).dot
                  }
                />
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>

        {deletedIds.length > 0 && (
          <p className="mt-3 text-xs font-medium text-error">
            {deletedIds.length} stage{deletedIds.length !== 1 ? 's' : ''} will
            be deleted when you save.
          </p>
        )}

        <div className="mt-4 flex items-center justify-between">
          {onCreateStage ? (
            <button
              type="button"
              onClick={handleCreateStage}
              disabled={isSaving}
              className={textLinkClass}
            >
              <PiPlus size={16} />
              Create Stage
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={handleResetToDefault}
            disabled={isSaving}
            className={textLinkClass}
          >
            <PiArrowCounterClockwise size={16} />
            Reset to Default
          </button>
        </div>
      </Modal>
    </Ds2026ButtonProvider>
  )
})
